import React, { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { UserContext } from '../../../contexts/UserContext';
import { SelectedPharmaciesContext } from '../../../contexts/SelectedPharmaciesContext';
import { SrxApi } from '../../../api/SrxApi';
import ReportFiltersCard from './components/ReportFiltersCard.js';
import ReportSetupModal from './components/ReportSetupModal.js';
import ReportPreviewTable from './components/ReportPreviewTable.js';
import ReportSqlModal from './components/ReportSqlModal.js';
import { EXPORT_FORMATS, downloadFile, formatDateForApi, getErrorMessage } from './pharmacyReportsHelpers.js';
import { hasRolePermission } from '../../../utility-services/rolePermissions.js';
import { DataFetchLoader } from '../../../components/Loader.js';
import './PharmacyReports.css';

function dedupeFacilities(facilities) {
    const seenFacIds = new Set();
    return (facilities || [])
        .filter((facility) => facility?.facName != null && facility?.facID != null)
        .filter((facility) => {
            if (seenFacIds.has(facility.facID)) return false;
            seenFacIds.add(facility.facID);
            return true;
        })
        .sort((a, b) => a.facName.localeCompare(b.facName))
        .map((facility) => ({ label: `(${facility.facID})  ${facility.facName} `, value: facility.facID }));
}

function PharmacyReports() {
    const { roles } = useContext(UserContext);
    const { pharmacies } = useContext(SelectedPharmaciesContext);

    const canView = hasRolePermission(roles, 'canViewManageReports');
    const canCreate = hasRolePermission(roles, 'canCreateManageReports');
    const canRunPermission = hasRolePermission(roles, 'canRunReports');

    const [reports, setReports] = useState([]);
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState('');
    const [selectedPharmacies, setSelectedPharmacies] = useState([]);
    const [selectedFacilities, setSelectedFacilities] = useState([]);
    const [values, setValues] = useState({});
    const [isRunning, setIsRunning] = useState(false);
    const [isReportSetupOpen, setIsReportSetupOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isSqlModalOpen, setIsSqlModalOpen] = useState(false);
    const [sqlText, setSqlText] = useState(null);
    const [isLoadingSql, setIsLoadingSql] = useState(false);

    const selectedReport = reports.find((report) => String(report.internalReportId) === selectedReportId);

    const pharmacyOptions = useMemo(() => {
        const seenPharmIds = new Set();
        return (pharmacies || [])
            .filter((pharmacy) => pharmacy?.pharmID != null)
            .filter((pharmacy) => {
                if (seenPharmIds.has(pharmacy.pharmID)) return false;
                seenPharmIds.add(pharmacy.pharmID);
                return true;
            })
            .map((pharmacy) => ({ label: pharmacy.pharmID, value: pharmacy.pharmID }));
    }, [pharmacies]);

    const facilityOptions = useMemo(() => {
        const pharmacyRows =
            selectedPharmacies.length > 0
                ? (pharmacies || []).filter((pharmacy) => selectedPharmacies.includes(pharmacy.pharmID))
                : pharmacies || [];
        return dedupeFacilities(pharmacyRows.flatMap((pharmacy) => pharmacy.facilities || []));
    }, [pharmacies, selectedPharmacies]);

    useEffect(() => {
        setSelectedFacilities((prev) => {
            const filtered = prev.filter((value) => facilityOptions.some((option) => option.value === value));
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [facilityOptions]);

    const loadReports = () => {
        setIsLoadingReports(true);
        SrxApi.get('/internalreports/availablereports')
            .then((res) =>
                setReports(
                    (Array.isArray(res?.data) ? res.data : []).sort((a, b) =>
                        (a.reportName || '').localeCompare(b.reportName || '')
                    )
                )
            )
            .catch((err) => {
                toast.error(err?.response?.data?.message || 'Error retrieving reports: ' + err.message);
                setReports([]);
            })
            .finally(() => setIsLoadingReports(false));
    };

    useEffect(() => {
        loadReports();
    }, []);

    const createMultiSelectHandlers = (setter, options) => ({
        onToggle: (value) => {
            setPreview(null);
            setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
        },
        onClear: () => {
            setPreview(null);
            setter([]);
        },
        onToggleAll: () => {
            setPreview(null);
            setter((prev) => (prev.length === options.length ? [] : options.map((option) => option.value)));
        },
    });

    const handleReportChange = (reportId) => {
        setSelectedReportId(reportId);
        setSelectedPharmacies([]);
        setSelectedFacilities([]);
        setValues({});
        setPreview(null);
    };

    const handleValueChange = (name, value) => {
        setPreview(null);
        setValues((prev) => ({ ...prev, [name]: value }));
    };

    const hasParameter = (parameterType) =>
        (selectedReport?.parameters || []).some((parameter) => parameter.parameterType === parameterType);

    const missingRequired = (selectedReport?.parameters || []).some(
        (parameter) =>
            (parameter.parameterType === 'Custom' || parameter.parameterType === 'Shared') &&
            parameter.isRequired &&
            !values[parameter.internalName]
    );

    const buildFacilitiesPayload = () => {
        if (selectedFacilities.length > 0) return selectedFacilities;
        if (selectedPharmacies.length > 0 && hasParameter('Pharmacies'))
            return facilityOptions.map((option) => option.value);
        return [];
    };

    const buildRunPayload = () => ({
        reportId: selectedReport.internalReportId,
        facilities: buildFacilitiesPayload(),
        pharmacies: selectedPharmacies,
        values,
    });

    const handleRun = () => {
        setIsRunning(true);
        const startedAt = Date.now();
        SrxApi.post('/internalreports/previewreport', buildRunPayload())
            .then((res) => {
                const data = res.data || {};
                const rows = Array.isArray(data.rows) ? data.rows : [];
                const columns = Array.isArray(data.columns) ? data.columns : [];
                const facNameIndex = columns.findIndex(
                    (column) => String(column ?? '').trim().toLowerCase() === 'facname'
                );
                const sortIndex = facNameIndex !== -1 ? facNameIndex : 0;
                const sortedRows = [...rows].sort((a, b) =>
                    String(a?.[sortIndex] ?? '').localeCompare(String(b?.[sortIndex] ?? ''))
                );
                setPreview({ ...data, rows: sortedRows });
            })
            .catch((err) => toast.error(err?.response?.data?.message || 'Error running report: ' + err.message))
            .finally(() => {
                const remaining = 1000 - (Date.now() - startedAt);
                setTimeout(() => setIsRunning(false), Math.max(0, remaining));
            });
    };

    const handleExport = (format) => {
        const extension = EXPORT_FORMATS.find((option) => option.value === format)?.extension || 'xlsx';

        setIsExporting(true);
        SrxApi.post('/internalreports/runreport', { ...buildRunPayload(), format }, { responseType: 'blob' })
            .then((res) =>
                downloadFile(res.data, `${selectedReport.reportName} ${formatDateForApi(new Date())}.${extension}`)
            )
            .catch(async (err) => toast.error(await getErrorMessage(err, 'Error exporting report: ' + err.message)))
            .finally(() => setIsExporting(false));
    };

    const handlePreviewSql = () => {
        setSqlText(null);
        setIsSqlModalOpen(true);
        setIsLoadingSql(true);
        const startedAt = Date.now();
        SrxApi.post('/internalreports/previewsql', buildRunPayload())
            .then((res) => setSqlText(res.data?.sqlText ?? ''))
            .catch((err) => {
                toast.error(err?.response?.data?.message || 'Error loading report SQL: ' + err.message);
                setIsSqlModalOpen(false);
            })
            .finally(() => {
                const remaining = 1000 - (Date.now() - startedAt);
                setTimeout(() => setIsLoadingSql(false), Math.max(0, remaining));
            });
    };

    const handleReportsChanged = () => {
        setSelectedReportId('');
        setValues({});
        setPreview(null);
        loadReports();
    };

    return (
        <div className="pharmacy-reports-page">
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="pharmacy-reports-title">Pharmacy Reports</h2>
                {canCreate && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary pharmacy-reports-btn"
                        onClick={() => setIsReportSetupOpen(true)}
                    >
                        Report Setup
                    </button>
                )}
            </div>

            {canView && (
                <ReportFiltersCard
                    reports={reports}
                    isLoadingReports={isLoadingReports}
                    selectedReportId={selectedReportId}
                    onReportChange={handleReportChange}
                    report={selectedReport}
                    pharmacy={{
                        options: pharmacyOptions,
                        selected: selectedPharmacies,
                        handlers: createMultiSelectHandlers(setSelectedPharmacies, pharmacyOptions),
                    }}
                    facility={{
                        options: facilityOptions,
                        selected: selectedFacilities,
                        handlers: createMultiSelectHandlers(setSelectedFacilities, facilityOptions),
                    }}
                    values={values}
                    onValueChange={handleValueChange}
                    canRun={Boolean(selectedReport) && !missingRequired}
                    canRunPermission={canRunPermission}
                    isRunning={isRunning}
                    onRun={handleRun}
                    canExport={Boolean(preview)}
                    isExporting={isExporting}
                    onExport={handleExport}
                    canPreviewSql={canCreate}
                    isLoadingSql={isLoadingSql}
                    onPreviewSql={handlePreviewSql}
                />
            )}

            {canView && isRunning && <DataFetchLoader name="Running report..." />}

            {canView && !isRunning && preview && selectedReport && (
                <ReportPreviewTable reportName={selectedReport.reportName} preview={preview} />
            )}

            <ReportSqlModal
                isOpen={isSqlModalOpen}
                reportName={selectedReport?.reportName}
                sqlText={sqlText}
                isLoading={isLoadingSql}
                onClose={() => setIsSqlModalOpen(false)}
            />

            {canCreate && (
                <ReportSetupModal
                    isOpen={isReportSetupOpen}
                    onClose={() => setIsReportSetupOpen(false)}
                    onReportsChanged={handleReportsChanged}
                />
            )}
        </div>
    );
}

export default PharmacyReports;
